// src/pages/ProfileSettings.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useTranslation } from "react-i18next";
import Select from "react-select";
import Cropper from "react-easy-crop";

import {
  FaCalendarAlt,
} from "react-icons/fa";

import {
  FiEye,
  FiEyeOff,
} from "react-icons/fi";

import {
  LuKey,
  LuUser,
} from "react-icons/lu";

import api from "../axiosConfig";
import "../styles/ProfileSettings.css";

import {
  useProfileSettingsData,
} from "../hooks/useProfileSettingsData";

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const getRoleOptions = (t) => [
  {
    value: "admin",
    label: t("roles.admin"),
  },
  {
    value: "dispatcher",
    label: t("roles.dispatcher"),
  },
  {
    value: "accountant",
    label: t("roles.accountant"),
  },
  {
    value: "driver",
    label: t("roles.driver"),
  },
  {
    value: "manager",
    label: t("roles.manager"),
  },
];


const isPdfUrl = (val) => {
  if (typeof val !== "string") {
    return false;
  }

  const clean =
    val
      .toLowerCase()
      .trim()
      .split("#")[0]
      .split("?")[0];

  return clean.endsWith(".pdf");
};


const absoluteFromApiBase = (path) => {
  if (!path) {
    return null;
  }

  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const base =
    (
      api.defaults?.baseURL ||
      ""
    ).replace(/\/$/, "");

  return base
    ? `${base}${path.startsWith("/") ? "" : "/"}${path}`
    : path;
};


const makePreview = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof File) {
    return URL.createObjectURL(
      value
    );
  }

  if (typeof value === "string") {
    return absoluteFromApiBase(
      value
    );
  }

  return null;
};


const niceFileName = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof File) {
    return value.name || "";
  }

  if (
    typeof value ===
    "string"
  ) {
    let name =
      value
        .split("#")[0]
        .split("?")[0];

    name =
      name
        .split("/")
        .pop() || "";

    try {
      name =
        decodeURIComponent(
          name
        );
    } catch {
      // ignore
    }

    name =
      name.replace(
        /^\d{10,}-/,
        ""
      );

    name =
      name.replace(
        /\.thumb(?=\.[a-z0-9]+$)/i,
        ""
      );

    return name;
  }

  return "";
};


const fullFromMaybeThumb = (
  value
) =>
  typeof value === "string"
    ? value.replace(
        /\.thumb(?=\.[a-z0-9]+(\?|#|$))/i,
        ""
      )
    : value;


const createImage = (
  url
) =>
  new Promise(
    (
      resolve,
      reject
    ) => {
      const image =
        new Image();

      image.addEventListener(
        "load",
        () =>
          resolve(image)
      );

      image.addEventListener(
        "error",
        reject
      );

      image.setAttribute(
        "crossOrigin",
        "anonymous"
      );

      image.src = url;
    }
  );


const getCroppedImg =
  async (
    imageSrc,
    pixelCrop
  ) => {
    const image =
      await createImage(
        imageSrc
      );

    const canvas =
      document.createElement(
        "canvas"
      );

    const ctx =
      canvas.getContext(
        "2d"
      );

    if (!ctx) {
      return null;
    }

    canvas.width =
      pixelCrop.width;

    canvas.height =
      pixelCrop.height;

    ctx.drawImage(
      image,
      pixelCrop.x,
      pixelCrop.y,
      pixelCrop.width,
      pixelCrop.height,
      0,
      0,
      pixelCrop.width,
      pixelCrop.height
    );

    return new Promise(
      (resolve) => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(null);
              return;
            }

            const file =
              new File(
                [blob],
                `profile-picture-${Date.now()}.jpg`,
                {
                  type:
                    "image/jpeg",
                }
              );

            resolve(file);
          },
          "image/jpeg",
          0.92
        );
      }
    );
  };


const formatDateForDisplay = (
  value
) => {
  if (!value) {
    return "—";
  }

  const parts =
    String(value)
      .slice(0, 10)
      .split("-");

  if (
    parts.length !== 3
  ) {
    return value;
  }

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};


const normalizeEmail = (
  value
) =>
  String(value || "")
    .trim()
    .toLowerCase();


const blankForm = {
  name: "",
  address: "",
  city: "",
  zipCode: "",
  country: "",
  idNumber: "",
  idExpiryDate: "",
  dob: "",
  phone: "",
  personalEmail: "",
  accountEmail: "",
  role: "",
  photo: null,
  idPhoto: null,

  currentPassword: "",
  newPassword: "",
  confirmNewPassword: "",
};


/* -------------------------------------------------------------------------- */
/*                                Main Component                              */
/* -------------------------------------------------------------------------- */

export default function ProfileSettings() {
  const {
    t: tProfile,
  } =
    useTranslation(
      "profile_settings"
    );

  const {
    t: tPersonal,
  } =
    useTranslation(
      "personal_info"
    );

  const {
    t: tAccount,
  } =
    useTranslation(
      "account_access"
    );


  /* ------------------------------------------------------------------------ */
  /*                                  Cache                                   */
  /* ------------------------------------------------------------------------ */

  const {
    data:
      settingsData,

    refresh:
      refreshSettings,

    patch:
      patchSettings,

    hasCachedData,
  } =
    useProfileSettingsData();


  const cachedProfile =
    settingsData?.profile ||
    null;


  const cachedAccountEmail =
    settingsData?.accountEmail ||
    "";

  /* ------------------------------------------------------------------------ */
  /*                                   State                                  */
  /* ------------------------------------------------------------------------ */

  const [
    form,
    setForm,
  ] =
    useState(() => ({
      ...blankForm,

      ...(cachedProfile ||
        {}),

      personalEmail:
        cachedProfile
          ?.personalEmail ||
        "",

      accountEmail:
        cachedAccountEmail,
    }));

  const loading =
    !hasCachedData;

  const [
    saving,
    setSaving,
  ] =
    useState(false);


  const [
    originalAccountEmail,
    setOriginalAccountEmail,
  ] =
    useState(
      normalizeEmail(
        cachedAccountEmail
      )
    );


  /* ------------------------------------------------------------------------ */
  /*                              Password visibility                         */
  /* ------------------------------------------------------------------------ */

  const [
    showCurrentPwd,
    setShowCurrentPwd,
  ] =
    useState(false);

  const [
    showNewPwd,
    setShowNewPwd,
  ] =
    useState(false);

  const [
    showConfirmPwd,
    setShowConfirmPwd,
  ] =
    useState(false);


  /* ------------------------------------------------------------------------ */
  /*                               Avatar editor                              */
  /* ------------------------------------------------------------------------ */

  const [
    avatarModalOpen,
    setAvatarModalOpen,
  ] =
    useState(false);

  const [
    avatarModalSrc,
    setAvatarModalSrc,
  ] =
    useState(null);

  const [
    avatarCrop,
    setAvatarCrop,
  ] =
    useState({
      x: 0,
      y: 0,
    });

  const [
    avatarZoom,
    setAvatarZoom,
  ] =
    useState(1);

  const [
    avatarCroppedPixels,
    setAvatarCroppedPixels,
  ] =
    useState(null);


  /* ------------------------------------------------------------------------ */
  /*                                   Refs                                   */
  /* ------------------------------------------------------------------------ */

  const avatarInputRef =
    useRef(null);

  const idPhotoInputRef =
    useRef(null);

  const idExpiryInputRef =
    useRef(null);

  const dobInputRef =
    useRef(null);


  /* ------------------------------------------------------------------------ */
  /*                               Derived state                              */
  /* ------------------------------------------------------------------------ */

  const roleOptions =
    useMemo(
      () =>
        getRoleOptions(
          tPersonal
        ),
      [tPersonal]
    );


  const selectedRoleOption =
    useMemo(
      () =>
        roleOptions.find(
          (option) =>
            option.value ===
            form.role
        ) || null,
      [
        roleOptions,
        form.role,
      ]
    );


  const selectedRoleLabel =
    selectedRoleOption
      ?.label ||
    form.role ||
    "—";


  const avatarPreview =
    useMemo(
      () =>
        makePreview(
          form.photo
        ),
      [form.photo]
    );


  const idPhotoPreview =
    useMemo(
      () =>
        makePreview(
          form.idPhoto
        ),
      [form.idPhoto]
    );


  const idPhotoLabel =
    useMemo(
      () =>
        niceFileName(
          form.idPhoto
        ),
      [form.idPhoto]
    );


  const avatarFileLabel =
    useMemo(
      () =>
        niceFileName(
          form.photo
        ),
      [form.photo]
    );


  const idBtnLabel =
    form.idPhoto
      ? tPersonal(
          "file.change"
        )
      : tPersonal(
          "file.choose"
        );


  const idPhotoHref =
    idPhotoPreview ||
    (
      typeof form.idPhoto ===
      "string"
        ? form.idPhoto
        : null
    );


  /* ------------------------------------------------------------------------ */
  /*                                Set field                                 */
  /* ------------------------------------------------------------------------ */

  const setField =
    (
      key,
      value
    ) => {
      setForm(
        (prev) => ({
          ...prev,
          [key]: value,
        })
      );
    };


  /* ------------------------------------------------------------------------ */
  /*                              Blob cleanup                                */
  /* ------------------------------------------------------------------------ */

  const prevAvatarBlobRef =
    useRef(null);

  useEffect(() => {
    if (
      typeof avatarPreview ===
        "string" &&
      avatarPreview.startsWith(
        "blob:"
      )
    ) {
      prevAvatarBlobRef.current =
        avatarPreview;
    }

    return () => {
      if (
        prevAvatarBlobRef.current
      ) {
        try {
          URL.revokeObjectURL(
            prevAvatarBlobRef.current
          );
        } catch {
          // ignore
        }

        prevAvatarBlobRef.current =
          null;
      }
    };
  }, [avatarPreview]);


  const prevIdBlobRef =
    useRef(null);

  useEffect(() => {
    if (
      typeof idPhotoPreview ===
        "string" &&
      idPhotoPreview.startsWith(
        "blob:"
      )
    ) {
      prevIdBlobRef.current =
        idPhotoPreview;
    }

    return () => {
      if (
        prevIdBlobRef.current
      ) {
        try {
          URL.revokeObjectURL(
            prevIdBlobRef.current
          );
        } catch {
          // ignore
        }

        prevIdBlobRef.current =
          null;
      }
    };
  }, [idPhotoPreview]);


  /* ------------------------------------------------------------------------ */
  /*                           Shared cache synchronization                    */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (
      !settingsData
    ) {
      return;
    }

    const nextProfile =
      settingsData.profile ||
      {};

    const nextAccountEmail =
      settingsData.accountEmail ||
      "";

    setOriginalAccountEmail(
      normalizeEmail(
        nextAccountEmail
      )
    );

    setForm(
      (
        previous
      ) => ({
        ...previous,

        ...nextProfile,

        personalEmail:
          nextProfile
            .personalEmail ||
          "",

        accountEmail:
          nextAccountEmail,

        /*
         * Never destroy passwords being typed because a
         * background/WebSocket refresh arrived.
         */
        currentPassword:
          previous.currentPassword,

        newPassword:
          previous.newPassword,

        confirmNewPassword:
          previous.confirmNewPassword,
      })
    );
  }, [
    settingsData,
  ]);


  /* ------------------------------------------------------------------------ */
  /*                                Date picker                               */
  /* ------------------------------------------------------------------------ */

  const openDatePicker =
    (
      ref
    ) => {
      const element =
        ref?.current;

      if (!element) {
        return;
      }

      if (
        typeof element.showPicker ===
        "function"
      ) {
        element.showPicker();
      } else {
        element.focus();
        element.click();
      }
    };


  /* ------------------------------------------------------------------------ */
  /*                              Avatar actions                              */
  /* ------------------------------------------------------------------------ */

  const openAvatarEditor =
    () => {
      setAvatarCrop({
        x: 0,
        y: 0,
      });

      setAvatarZoom(1);

      setAvatarCroppedPixels(
        null
      );


      if (avatarPreview) {
        setAvatarModalSrc(
          fullFromMaybeThumb(
            avatarPreview
          )
        );

        setAvatarModalOpen(
          true
        );

        return;
      }


      avatarInputRef.current
        ?.click();
    };


  const closeAvatarModal =
    () => {
      setAvatarModalOpen(
        false
      );

      setAvatarCrop({
        x: 0,
        y: 0,
      });

      setAvatarZoom(1);

      setAvatarCroppedPixels(
        null
      );
    };


  const onAvatarCropComplete =
    useCallback(
      (
        _,
        croppedAreaPixels
      ) => {
        setAvatarCroppedPixels(
          croppedAreaPixels
        );
      },
      []
    );


  const handleAvatarFileSelected =
    (
      file
    ) => {
      if (!file) {
        return;
      }

      const previewUrl =
        URL.createObjectURL(
          file
        );

      setField(
        "photo",
        file
      );

      setAvatarModalSrc(
        previewUrl
      );

      setAvatarCrop({
        x: 0,
        y: 0,
      });

      setAvatarZoom(1);

      setAvatarCroppedPixels(
        null
      );

      setAvatarModalOpen(
        true
      );
    };


  const applyAvatarCrop =
    async () => {
      if (
        !avatarModalSrc ||
        !avatarCroppedPixels
      ) {
        closeAvatarModal();
        return;
      }


      try {
        const croppedFile =
          await getCroppedImg(
            avatarModalSrc,
            avatarCroppedPixels
          );

        if (croppedFile) {
          setField(
            "photo",
            croppedFile
          );
        }

        closeAvatarModal();
      } catch (error) {
        console.error(
          "[ProfileSettings] avatar crop error:",
          error
        );

        alert(
          tPersonal(
            "avatarEditor.errors.crop_failed"
          )
        );
      }
    };


  /* ------------------------------------------------------------------------ */
  /*                                   Save                                   */
  /* ------------------------------------------------------------------------ */

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();


      const wantsPasswordChange =
        Boolean(
          form.newPassword
            ?.trim()
        );


      if (
        wantsPasswordChange &&
        !form.currentPassword
          ?.trim()
      ) {
        alert(
          tAccount(
            "toasts.current_password_required",
            "Current password is required to change password."
          )
        );

        return;
      }


      if (
        wantsPasswordChange &&
        !form.confirmNewPassword
          ?.trim()
      ) {
        alert(
          tAccount(
            "toasts.confirm_password_required",
            "Please confirm the new password."
          )
        );

        return;
      }


      if (
        wantsPasswordChange &&
        form.newPassword !==
          form.confirmNewPassword
      ) {
        alert(
          tAccount(
            "toasts.password_mismatch",
            "New password and confirmation do not match."
          )
        );

        return;
      }


      setSaving(true);


      try {
        /* -------------------------- Personal information ------------------------- */

        const fd =
          new FormData();

        fd.append(
          "name",
          form.name ||
            ""
        );

        fd.append(
          "address",
          form.address ||
            ""
        );

        fd.append(
          "city",
          form.city ||
            ""
        );

        fd.append(
          "postalCode",
          form.zipCode ||
            ""
        );

        fd.append(
          "country",
          form.country ||
            ""
        );

        fd.append(
          "idNumber",
          form.idNumber ||
            ""
        );

        fd.append(
          "idExpiryDate",
          form.idExpiryDate ||
            ""
        );

        fd.append(
          "dateOfBirth",
          form.dob ||
            ""
        );

        fd.append(
          "phoneNumber",
          form.phone ||
            ""
        );

        fd.append(
          "email",
          normalizeEmail(
            form.personalEmail
          )
        );

        fd.append(
          "role",
          (
            form.role ||
            ""
          ).toLowerCase()
        );


        if (
          form.photo instanceof
          File
        ) {
          fd.append(
            "profilePicture",
            form.photo
          );
        } else if (
          form.photo === null
        ) {
          fd.append(
            "profilePictureRemove",
            "1"
          );
        }


        if (
          form.idPhoto instanceof
          File
        ) {
          fd.append(
            "idPhoto",
            form.idPhoto
          );
        }


        await api.put(
          "/users/profile",
          fd,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );


        /* ------------------------------ Account access ---------------------------- */

        const nextAccountEmail =
          normalizeEmail(
            form.accountEmail
          );


        const accountEmailChanged =
          nextAccountEmail !==
          normalizeEmail(
            originalAccountEmail
          );


        if (
          accountEmailChanged ||
          wantsPasswordChange
        ) {
          await api.patch(
            "/auth/account-access",
            {
              email:
                nextAccountEmail,

              currentPassword:
                form.currentPassword,

              newPassword:
                form.newPassword,

              confirmNewPassword:
                form.confirmNewPassword,
            }
          );
        }


        /* ---------------------------- Refresh final data --------------------------- */

        const [
          profileRefresh,
          accountRefresh,
        ] =
          await Promise.all([
            api.get(
              "/users/profile"
            ),

            api.get(
              "/auth/account-access"
            ),
          ]);


        const data =
          profileRefresh
            ?.data ||
          {};


        const refreshedAccountEmail =
          accountRefresh
            ?.data
            ?.email ||
          nextAccountEmail;


        const nextForm = {
          name:
            data.name ||
            "",

          address:
            data.address ||
            "",

          city:
            data.city ||
            "",

          zipCode:
            data.postalCode ||
            "",

          country:
            data.country ||
            "",

          idNumber:
            data.idNumber ||
            "",

          idExpiryDate:
            (
              data.idExpiryDate ||
              ""
            ).slice(
              0,
              10
            ),

          dob:
            (
              data.dateOfBirth ||
              ""
            ).slice(
              0,
              10
            ),

          phone:
            data.phoneNumber ||
            "",

          personalEmail:
            data.email ||
            normalizeEmail(
              form.personalEmail
            ),

          accountEmail:
            refreshedAccountEmail,

          role:
            (
              data.role ||
              ""
            ).toLowerCase(),

          photo:
            data.profilePictureUrl ||
            absoluteFromApiBase(
              data.profilePicture
            ) ||
            null,

          idPhoto:
            data.idPhotoUrl ||
            absoluteFromApiBase(
              data.idPhoto
            ) ||
            null,

          currentPassword:
            "",

          newPassword:
            "",

          confirmNewPassword:
            "",
        };


        setForm(
          nextForm
        );


        setOriginalAccountEmail(
          normalizeEmail(
            nextForm.accountEmail
          )
        );


        patchSettings(
          (
            current
          ) => ({
            ...current,

            profile: {
              name:
                nextForm.name,

              address:
                nextForm.address,

              city:
                nextForm.city,

              zipCode:
                nextForm.zipCode,

              country:
                nextForm.country,

              idNumber:
                nextForm.idNumber,

              idExpiryDate:
                nextForm.idExpiryDate,

              dob:
                nextForm.dob,

              phone:
                nextForm.phone,

              personalEmail:
                nextForm.personalEmail,

              role:
                nextForm.role,

              photo:
                nextForm.photo,

              idPhoto:
                nextForm.idPhoto,
            },

            accountEmail:
              normalizeEmail(
                nextForm.accountEmail
              ),

            fetchedAt:
              Date.now(),
          })
        );


        localStorage.setItem(
          "auth_user",
          JSON.stringify({
            name:
              nextForm.name,

            email:
              nextForm.personalEmail,
          })
        );


        window.dispatchEvent(
          new CustomEvent(
            "profileSaved",
            {
              detail: {
                name:
                  nextForm.name,

                email:
                  nextForm.personalEmail,
              },
            }
          )
        );


        alert(
          tPersonal(
            "toasts.saved_success"
          )
        );
      } catch (error) {
        console.error(
          "[ProfileSettings] save error:",
          error
        );

        alert(
          error?.response
            ?.data
            ?.error ||
          tPersonal(
            "toasts.saved_error"
          )
        );
      } finally {
        setSaving(false);
      }
    };


  /* ------------------------------------------------------------------------ */
  /*                                   JSX                                    */
  /* ------------------------------------------------------------------------ */

  return (
    <div
      className="profile-settings"
      aria-busy={
        loading
      }
    >
      {/* ------------------------------------------------------------ */}
      {/* Header                                                       */}
      {/* ------------------------------------------------------------ */}

      <div className="profile-settings__header">
        <h1 className="profile-settings__title">
          {tProfile(
            "title"
          )}
        </h1>

        <p className="profile-settings__subtitle">
          {tProfile(
            "subtitle"
          )}
        </p>
      </div>


      {/* ------------------------------------------------------------ */}
      {/* Summary                                                      */}
      {/* ------------------------------------------------------------ */}

      <section className="profile-settings__summary">
        <div className="profile-settings__identity">
          <button
            type="button"
            className="profile-settings__avatarButton"
            onClick={
              openAvatarEditor
            }
            aria-label={
              tPersonal(
                "fields.profilePhoto.aria"
              )
            }
          >
            <div className="profile-settings__avatar">
              {avatarPreview ? (
                <img
                  crossOrigin="anonymous"
                  src={
                    avatarPreview
                  }
                  alt={
                    tPersonal(
                      "fields.profilePhoto.alt"
                    )
                  }
                  decoding="sync"
                  fetchPriority="high"
                />
              ) : (
                <LuUser />
              )}
            </div>

            <span className="profile-settings__avatarEdit">
              {tPersonal(
                "avatarEditor.edit_hint",
                "Editar"
              )}
            </span>
          </button>


          <div className="profile-settings__identityText">
            <h2>
              {form.name ||
                "—"}
            </h2>

            <p>
              {form.accountEmail ||
                form.personalEmail ||
                "—"}
            </p>

            <span className="profile-settings__roleBadge">
              {
                selectedRoleLabel
              }
            </span>
          </div>
        </div>


        <div className="profile-settings__summaryMeta">
          <div className="profile-settings__summaryItem">
            <span className="profile-settings__summaryItemLabel">
              {tPersonal(
                "fields.idNumber.label"
              )}
            </span>

            <strong>
              {form.idNumber ||
                "—"}
            </strong>
          </div>


          <div className="profile-settings__summaryDivider" />


          <div className="profile-settings__summaryItem">
            <span className="profile-settings__summaryItemLabel">
              {tPersonal(
                "fields.idExpiryDate.label"
              )}
            </span>

            <strong>
              {formatDateForDisplay(
                form.idExpiryDate
              )}
            </strong>
          </div>


          <div className="profile-settings__summaryDivider" />


          <div className="profile-settings__summaryItem">
            <span className="profile-settings__summaryItemLabel">
              {tPersonal(
                "fields.dob.label"
              )}
            </span>

            <strong>
              {formatDateForDisplay(
                form.dob
              )}
            </strong>
          </div>
        </div>
      </section>


      <form
        className="profile-settings__form"
        onSubmit={
          handleSubmit
        }
      >
        {/* ---------------------------------------------------------- */}
        {/* Personal information                                       */}
        {/* ---------------------------------------------------------- */}

        <section className="profile-settings__card">
          <div className="profile-settings__sectionHeader">
            <div className="profile-settings__sectionIcon">
              <LuUser />
            </div>

            <div>
              <h2>
                {tProfile(
                  "tiles.personal_info.title"
                )}
              </h2>

              <p>
                {tProfile(
                  "tiles.personal_info.desc"
                )}
              </p>
            </div>
          </div>


          <div className="profile-settings__personalGrid">
            {/* Photo manager — no duplicated avatar */}

            <div className="profile-settings__field profile-settings__field--photo">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.profilePhoto.label"
                )}
              </label>

              <div className="profile-settings__photoManager">
                <div className="profile-settings__photoFile">
                  <span className="profile-settings__photoFileCaption">
                    {avatarFileLabel
                      ? tPersonal(
                          "file.current",
                          "Ficheiro atual"
                        )
                      : tPersonal(
                          "file.none_selected"
                        )}
                  </span>

                  <strong
                    title={
                      avatarFileLabel
                    }
                  >
                    {avatarFileLabel ||
                      tPersonal(
                        "file.empty",
                        "Sem fotografia"
                      )}
                  </strong>
                </div>


                <div className="profile-settings__photoActions">
                  <button
                    type="button"
                    className="profile-settings__smallButton"
                    onClick={
                      openAvatarEditor
                    }
                    disabled={
                      loading ||
                      saving
                    }
                  >
                    {tPersonal(
                      "photo.change"
                    )}
                  </button>


                  <button
                    type="button"
                    className="profile-settings__smallButton profile-settings__smallButton--danger"
                    onClick={() =>
                      setField(
                        "photo",
                        null
                      )
                    }
                    disabled={
                      loading ||
                      saving ||
                      !form.photo
                    }
                  >
                    {tPersonal(
                      "photo.remove",
                      "Remover"
                    )}
                  </button>
                </div>
              </div>


              <input
                ref={
                  avatarInputRef
                }
                type="file"
                accept="image/*"
                hidden
                onChange={(
                  event
                ) => {
                  handleAvatarFileSelected(
                    event.target
                      .files?.[0] ||
                      null
                  );

                  event.target.value =
                    "";
                }}
              />
            </div>


            <div className="profile-settings__field profile-settings__field--name">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.name.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.name
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "name",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.name.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--idNumber">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.idNumber.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.idNumber
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "idNumber",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.idNumber.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--address">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.address.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.address
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "address",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.address.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--zip">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.zipCode.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.zipCode
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "zipCode",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.zipCode.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--city">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.city.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.city
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "city",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.city.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--country">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.country.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                required
                value={
                  form.country
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "country",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.country.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--phone">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.phone.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                type="tel"
                value={
                  form.phone
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "phone",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.phone.placeholder"
                  )
                }
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--email">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.email.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                type="email"
                required
                value={
                  form.personalEmail
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "personalEmail",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.email.placeholder"
                  )
                }
                autoComplete="email"
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--role">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.role.label"
                )}
              </label>

              <Select
                options={
                  roleOptions
                }
                value={
                  selectedRoleOption
                }
                onChange={(
                  option
                ) =>
                  setField(
                    "role",
                    option?.value ||
                      ""
                  )
                }
                placeholder={
                  tPersonal(
                    "fields.role.placeholder"
                  )
                }
                isClearable
                isDisabled={
                  loading ||
                  saving
                }
                className="profile-settings-select"
                classNamePrefix="profile-settings-select"
              />
            </div>


            <div className="profile-settings__field profile-settings__field--idPhoto">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.idPhoto.label"
                )}
              </label>

              <div className="profile-settings__filePicker">
                <button
                  type="button"
                  className="profile-settings__fileButton"
                  onClick={() =>
                    idPhotoInputRef
                      .current
                      ?.click()
                  }
                  disabled={
                    loading ||
                    saving
                  }
                >
                  {
                    idBtnLabel
                  }
                </button>

                <span
                  className={`profile-settings__fileName ${
                    idPhotoLabel
                      ? "profile-settings__fileName--active"
                      : ""
                  }`}
                >
                  {idPhotoLabel ? (
                    idPhotoHref ? (
                      <a
                        href={
                          idPhotoHref
                        }
                        target="_blank"
                        rel="noreferrer"
                        title={
                          isPdfUrl(
                            idPhotoHref
                          )
                            ? tPersonal(
                                "links.open_pdf"
                              )
                            : tPersonal(
                                "links.open_image"
                              )
                        }
                      >
                        {
                          idPhotoLabel
                        }
                      </a>
                    ) : (
                      idPhotoLabel
                    )
                  ) : (
                    tPersonal(
                      "file.none_selected"
                    )
                  )}
                </span>
              </div>


              <input
                ref={
                  idPhotoInputRef
                }
                type="file"
                accept="image/*,application/pdf"
                hidden
                onChange={(
                  event
                ) =>
                  setField(
                    "idPhoto",
                    event.target
                      .files?.[0] ||
                      null
                  )
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--expiry">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.idExpiryDate.label"
                )}
              </label>

              <div
                className="profile-settings__dateField"
                onClick={() =>
                  openDatePicker(
                    idExpiryInputRef
                  )
                }
              >
                <input
                  ref={
                    idExpiryInputRef
                  }
                  className="profile-settings__dateInput"
                  type="date"
                  value={
                    form.idExpiryDate
                  }
                  onChange={(
                    event
                  ) =>
                    setField(
                      "idExpiryDate",
                      event.target
                        .value
                    )
                  }
                  disabled={
                    loading ||
                    saving
                  }
                />

                <button
                  type="button"
                  className="profile-settings__dateButton"
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();

                    openDatePicker(
                      idExpiryInputRef
                    );
                  }}
                  disabled={
                    loading ||
                    saving
                  }
                >
                  <FaCalendarAlt />
                </button>
              </div>
            </div>


            <div className="profile-settings__field profile-settings__field--dob">
              <label className="profile-settings__label">
                {tPersonal(
                  "fields.dob.label"
                )}
              </label>

              <div
                className="profile-settings__dateField"
                onClick={() =>
                  openDatePicker(
                    dobInputRef
                  )
                }
              >
                <input
                  ref={
                    dobInputRef
                  }
                  className="profile-settings__dateInput"
                  type="date"
                  value={
                    form.dob
                  }
                  onChange={(
                    event
                  ) =>
                    setField(
                      "dob",
                      event.target
                        .value
                    )
                  }
                  disabled={
                    loading ||
                    saving
                  }
                />

                <button
                  type="button"
                  className="profile-settings__dateButton"
                  onClick={(
                    event
                  ) => {
                    event.stopPropagation();

                    openDatePicker(
                      dobInputRef
                    );
                  }}
                  disabled={
                    loading ||
                    saving
                  }
                >
                  <FaCalendarAlt />
                </button>
              </div>
            </div>
          </div>
        </section>


        {/* ---------------------------------------------------------- */}
        {/* Account access                                             */}
        {/* ---------------------------------------------------------- */}

        <section className="profile-settings__card">
          <div className="profile-settings__sectionHeader">
            <div className="profile-settings__sectionIcon">
              <LuKey />
            </div>

            <div>
              <h2>
                {tProfile(
                  "tiles.account_access.title"
                )}
              </h2>

              <p>
                {tProfile(
                  "tiles.account_access.desc"
                )}
              </p>
            </div>
          </div>


          <div className="profile-settings__accountGrid">
            <div className="profile-settings__field profile-settings__field--accountEmail">
              <label className="profile-settings__label">
                {tAccount(
                  "fields.email.label"
                )}
              </label>

              <input
                className="profile-settings__input"
                type="email"
                required
                value={
                  form.accountEmail
                }
                onChange={(
                  event
                ) =>
                  setField(
                    "accountEmail",
                    event.target
                      .value
                  )
                }
                placeholder={
                  tAccount(
                    "fields.email.placeholder"
                  )
                }
                autoComplete="email"
                disabled={
                  loading ||
                  saving
                }
              />
            </div>


            <div className="profile-settings__field profile-settings__field--currentPassword">
              <label className="profile-settings__label">
                {tAccount(
                  "fields.current_password.label",
                  "Current Password"
                )}
              </label>

              <div className="profile-settings__passwordWrap">
                <input
                  className="profile-settings__input"
                  type={
                    showCurrentPwd
                      ? "text"
                      : "password"
                  }
                  value={
                    form.currentPassword
                  }
                  onChange={(
                    event
                  ) =>
                    setField(
                      "currentPassword",
                      event.target
                        .value
                    )
                  }
                  placeholder={
                    tAccount(
                      "fields.current_password.placeholder",
                      "Type your current password"
                    )
                  }
                  autoComplete="current-password"
                  disabled={
                    loading ||
                    saving
                  }
                />

                <button
                  type="button"
                  className="profile-settings__passwordToggle"
                  onClick={() =>
                    setShowCurrentPwd(
                      (prev) =>
                        !prev
                    )
                  }
                >
                  {showCurrentPwd ? (
                    <FiEyeOff />
                  ) : (
                    <FiEye />
                  )}
                </button>
              </div>

              <small className="profile-settings__hint">
                {tAccount(
                  "fields.current_password.hint",
                  "Only required if you want to change your password."
                )}
              </small>
            </div>


            <div className="profile-settings__field profile-settings__field--newPassword">
              <label className="profile-settings__label">
                {tAccount(
                  "fields.new_password.label",
                  "New Password"
                )}
              </label>

              <div className="profile-settings__passwordWrap">
                <input
                  className="profile-settings__input"
                  type={
                    showNewPwd
                      ? "text"
                      : "password"
                  }
                  value={
                    form.newPassword
                  }
                  onChange={(
                    event
                  ) =>
                    setField(
                      "newPassword",
                      event.target
                        .value
                    )
                  }
                  placeholder={
                    tAccount(
                      "fields.new_password.placeholder",
                      "Leave blank to keep current password"
                    )
                  }
                  autoComplete="new-password"
                  disabled={
                    loading ||
                    saving
                  }
                />

                <button
                  type="button"
                  className="profile-settings__passwordToggle"
                  onClick={() =>
                    setShowNewPwd(
                      (prev) =>
                        !prev
                    )
                  }
                >
                  {showNewPwd ? (
                    <FiEyeOff />
                  ) : (
                    <FiEye />
                  )}
                </button>
              </div>
            </div>


            <div className="profile-settings__field profile-settings__field--confirmPassword">
              <label className="profile-settings__label">
                {tAccount(
                  "fields.confirm_new_password.label",
                  "Confirm New Password"
                )}
              </label>

              <div className="profile-settings__passwordWrap">
                <input
                  className="profile-settings__input"
                  type={
                    showConfirmPwd
                      ? "text"
                      : "password"
                  }
                  value={
                    form.confirmNewPassword
                  }
                  onChange={(
                    event
                  ) =>
                    setField(
                      "confirmNewPassword",
                      event.target
                        .value
                    )
                  }
                  placeholder={
                    tAccount(
                      "fields.confirm_new_password.placeholder",
                      "Repeat the new password"
                    )
                  }
                  autoComplete="new-password"
                  disabled={
                    loading ||
                    saving
                  }
                />

                <button
                  type="button"
                  className="profile-settings__passwordToggle"
                  onClick={() =>
                    setShowConfirmPwd(
                      (prev) =>
                        !prev
                    )
                  }
                >
                  {showConfirmPwd ? (
                    <FiEyeOff />
                  ) : (
                    <FiEye />
                  )}
                </button>
              </div>
            </div>
          </div>
        </section>


        {/* ---------------------------------------------------------- */}
        {/* Actions                                                    */}
        {/* ---------------------------------------------------------- */}

        <div className="profile-settings__actions">
          <button
            type="button"
            className="profile-settings__button profile-settings__button--ghost"
            onClick={() =>
              window.history.back()
            }
            disabled={
              saving
            }
          >
            {tPersonal(
              "actions.cancel"
            )}
          </button>


          <button
            type="submit"
            className="profile-settings__button profile-settings__button--primary"
            disabled={
              loading ||
              saving
            }
          >
            {saving
              ? tPersonal(
                  "actions.saving"
                )
              : tPersonal(
                  "actions.save"
                )}
          </button>
        </div>
      </form>


      {/* ------------------------------------------------------------ */}
      {/* Avatar crop modal                                            */}
      {/* ------------------------------------------------------------ */}

      {avatarModalOpen && (
        <div
          className="profile-settings__avatarModalOverlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={
            closeAvatarModal
          }
        >
          <div
            className="profile-settings__avatarModal"
            onMouseDown={(
              event
            ) =>
              event.stopPropagation()
            }
          >
            <div className="profile-settings__avatarModalHeader">
              <div>
                <h2>
                  {tPersonal(
                    "avatarEditor.title"
                  )}
                </h2>

                <p>
                  {tPersonal(
                    "avatarEditor.subtitle"
                  )}
                </p>
              </div>

              <button
                type="button"
                className="profile-settings__avatarModalClose"
                onClick={
                  closeAvatarModal
                }
              >
                ×
              </button>
            </div>


            <div className="profile-settings__avatarCropArea">
              {avatarModalSrc ? (
                <Cropper
                  image={
                    avatarModalSrc
                  }
                  crop={
                    avatarCrop
                  }
                  zoom={
                    avatarZoom
                  }
                  aspect={1}
                  cropShape="round"
                  showGrid={
                    false
                  }
                  onCropChange={
                    setAvatarCrop
                  }
                  onZoomChange={
                    setAvatarZoom
                  }
                  onCropComplete={
                    onAvatarCropComplete
                  }
                />
              ) : (
                <div className="profile-settings__avatarCropEmpty">
                  {tPersonal(
                    "avatarEditor.empty"
                  )}
                </div>
              )}
            </div>


            <div className="profile-settings__avatarZoomRow">
              <span>
                {tPersonal(
                  "avatarEditor.zoom_out"
                )}
              </span>

              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={
                  avatarZoom
                }
                onChange={(
                  event
                ) =>
                  setAvatarZoom(
                    Number(
                      event.target
                        .value
                    )
                  )
                }
              />

              <span>
                {tPersonal(
                  "avatarEditor.zoom_in"
                )}
              </span>
            </div>


            <div className="profile-settings__avatarModalActions">
              <button
                type="button"
                className="profile-settings__button profile-settings__button--ghost"
                onClick={() =>
                  avatarInputRef
                    .current
                    ?.click()
                }
              >
                {tPersonal(
                  "avatarEditor.choose_new"
                )}
              </button>


              <div>
                <button
                  type="button"
                  className="profile-settings__button profile-settings__button--ghost"
                  onClick={
                    closeAvatarModal
                  }
                >
                  {tPersonal(
                    "actions.cancel"
                  )}
                </button>

                <button
                  type="button"
                  className="profile-settings__button profile-settings__button--primary"
                  onClick={
                    applyAvatarCrop
                  }
                >
                  {tPersonal(
                    "avatarEditor.apply"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}